import { resolveStopPrice, resolveTakeProfitPrice, tagsStop, tagsTarget } from './fillModel';
import { wholeQty } from './positionMode';

export function splitQty(total, takeProfits) {
  const whole = wholeQty(total);
  if (!takeProfits?.length) return whole ? [whole] : [];
  const shares = new Array(takeProfits.length).fill(0);
  if (whole < 1) return shares;
  const weights = takeProfits.map((tp) => {
    const pct = Number(tp.qtyPct);
    return Number.isFinite(pct) && pct > 0 ? pct : 0;
  });
  const weightSum = weights.reduce((s, v) => s + v, 0);
  if (weightSum <= 0) {
    shares[0] = whole;
    return shares;
  }
  const raw = weights.map((w) => (w / weightSum) * whole);
  const floors = raw.map((n) => Math.floor(n + 1e-9));
  let leftover = whole - floors.reduce((s, v) => s + v, 0);
  const order = raw
    .map((n, i) => ({ i, frac: n - Math.floor(n + 1e-9) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of order) {
    if (leftover <= 0) break;
    floors[i] += 1;
    leftover -= 1;
  }
  return floors;
}

export function pnlFor(side, entryPrice, exitPrice, qty, pointValue = 1) {
  const pv = Number(pointValue) > 0 ? Number(pointValue) : 1;
  const dir = side === 'long' ? 1 : -1;
  return dir * (Number(exitPrice) - Number(entryPrice)) * Number(qty) * pv;
}

export function openPosition({
  id,
  side,
  qty = 1,
  entryPrice,
  entryIndex,
  entryTime,
  stopPrice,
  atr,
  atrMultiple,
  takeProfits = [],
  pointValue = 1,
}) {
  const resolvedStop = resolveStopPrice(side, entryPrice, { stopPrice, atr, atrMultiple });
  const rValue = side === 'long' ? entryPrice - resolvedStop : resolvedStop - entryPrice;
  const absR = Number.isFinite(rValue) && rValue > 0 ? rValue : null;
  const size = wholeQty(qty);
  const pv = Number(pointValue) > 0 ? Number(pointValue) : 1;
  const tpQtys = splitQty(size, takeProfits);
  const legs = (takeProfits || [])
    .map((tp, idx) => ({
      id: tp.id || `tp${idx + 1}`,
      rMultiple: Number(tp.rMultiple) || (idx + 1),
      qtyPct: tp.qtyPct,
      qty: tpQtys[idx] || 0,
      price: Number.isFinite(tp.price)
        ? tp.price
        : (absR != null
          ? resolveTakeProfitPrice(side, entryPrice, absR, Number(tp.rMultiple) || (idx + 1))
          : null),
      filledIndex: null,
      filledPrice: null,
      filledQty: 0,
    }))
    .filter((tp) => tp.qty > 0);

  return {
    id,
    side,
    qty: size,
    remainingQty: size,
    entryPrice,
    entryIndex,
    entryTime,
    stopPrice: resolvedStop,
    rValue: absR,
    risk: absR > 0 ? size * absR * pv : 0,
    takeProfits: legs,
    exits: [],
    status: 'open',
    exitIndex: null,
    exitTime: null,
    exitPrice: null,
    exitReason: null,
    pnl: 0,
    pointValue: Number(pointValue) > 0 ? Number(pointValue) : 1,
  };
}

function markClosed(position, bar, index, price, reason) {
  position.remainingQty = 0;
  position.status = 'closed';
  position.exitIndex = index;
  position.exitTime = bar.timestamp;
  position.exitPrice = price;
  position.exitReason = reason;
  return position;
}

function closeRemaining(position, bar, index, price, reason) {
  const qty = position.remainingQty;
  if (!(qty > 0)) {
    position.status = 'closed';
    return position;
  }
  position.exits.push({
    index,
    time: bar.timestamp,
    timeSec: bar.timeSec,
    price,
    qty,
    reason,
  });
  position.pnl += pnlFor(position.side, position.entryPrice, price, qty, position.pointValue);
  return markClosed(position, bar, index, price, reason);
}

export function applyBarFills(position, bar, index) {
  if (!position || position.status === 'closed' || !(position.remainingQty > 0)) return position;

  const stopHit = tagsStop(bar, position.side, position.stopPrice);
  if (stopHit) {
    return closeRemaining(position, bar, index, position.stopPrice, 'stop');
  }

  const unfilled = position.takeProfits.filter((tp) => tp.filledIndex == null && Number.isFinite(tp.price) && tp.qty > 0);
  const hits = unfilled.filter((tp) => tagsTarget(bar, position.side, tp.price));
  hits.sort((a, b) => (position.side === 'long' ? a.price - b.price : b.price - a.price));

  for (const tp of hits) {
    const fillQty = Math.min(tp.qty - tp.filledQty, position.remainingQty);
    if (!(fillQty > 0)) continue;
    tp.filledIndex = index;
    tp.filledPrice = tp.price;
    tp.filledQty += fillQty;
    position.exits.push({
      index,
      time: bar.timestamp,
      timeSec: bar.timeSec,
      price: tp.price,
      qty: fillQty,
      reason: 'tp',
      tpId: tp.id,
    });
    position.pnl += pnlFor(position.side, position.entryPrice, tp.price, fillQty, position.pointValue);
    position.remainingQty -= fillQty;
    if (position.remainingQty <= 1e-9) {
      position.remainingQty = 0;
      position.status = 'closed';
      position.exitIndex = index;
      position.exitTime = bar.timestamp;
      position.exitPrice = tp.price;
      position.exitReason = 'tp';
      break;
    }
    position.status = 'partial';
  }
  return position;
}

export function closePosition(position, bar, index, reason = 'signal', price) {
  if (!position || position.status === 'closed') return position;
  const fill = Number.isFinite(price) ? price : bar.open;
  return closeRemaining(position, bar, index, fill, reason);
}

/** Add size to an open position at `fillPrice` (VWAP entry). */
export function addToPosition(position, bar, addQty, fillPrice) {
  if (!position || position.status === 'closed') return position;
  const qty = wholeQty(addQty);
  const price = Number.isFinite(fillPrice) ? fillPrice : bar.open;
  if (!qty || !Number.isFinite(price)) return position;
  const oldQty = position.qty;
  position.entryPrice = (position.entryPrice * oldQty + price * qty) / (oldQty + qty);
  position.qty += qty;
  position.remainingQty += qty;
  if (Number.isFinite(position.stopPrice)) {
    const rValue = position.side === 'long'
      ? position.entryPrice - position.stopPrice
      : position.stopPrice - position.entryPrice;
    position.rValue = rValue > 0 ? rValue : null;
  }
  const pv = Number(position.pointValue) > 0 ? Number(position.pointValue) : 1;
  position.risk = position.rValue > 0 ? position.qty * position.rValue * pv : 0;
  if (position.status === 'closed') position.status = 'open';
  return position;
}

/** Close `qty` of an open position at the bar open. */
export function reducePosition(position, bar, index, qty, reason = 'signal') {
  if (!position || position.status === 'closed') return position;
  const fillQty = Math.min(position.remainingQty, Number(qty) || 0);
  if (!(fillQty > 0)) return position;
  const price = bar.open;
  position.exits.push({
    index,
    time: bar.timestamp,
    timeSec: bar.timeSec,
    price,
    qty: fillQty,
    reason,
  });
  position.pnl += pnlFor(position.side, position.entryPrice, price, fillQty, position.pointValue);
  position.remainingQty -= fillQty;
  if (position.remainingQty <= 1e-9) {
    return markClosed(position, bar, index, price, reason);
  }
  position.status = 'partial';
  return position;
}

export function snapshotPosition(position) {
  if (!position) return null;
  return {
    id: position.id,
    side: position.side,
    qty: position.qty,
    remainingQty: position.remainingQty,
    entryPrice: position.entryPrice,
    entryIndex: position.entryIndex,
    entryTime: position.entryTime,
    stopPrice: position.stopPrice,
    rValue: position.rValue,
    risk: Number(position.risk) > 0 ? position.risk : 0,
    takeProfits: (position.takeProfits || []).map((tp) => ({ ...tp })),
    status: position.status,
    pnl: position.pnl,
    pointValue: position.pointValue,
  };
}

export function finalizeTrade(position) {
  const pv = Number(position.pointValue) > 0 ? Number(position.pointValue) : 1;
  const risk = Number(position.risk) > 0
    ? Number(position.risk)
    : (position.rValue > 0 ? position.qty * position.rValue * pv : 0);
  const r = risk > 0 ? position.pnl / risk : null;
  return {
    ...snapshotPosition(position),
    exits: position.exits.slice(),
    exitIndex: position.exitIndex,
    exitTime: position.exitTime,
    exitPrice: position.exitPrice,
    exitReason: position.exitReason,
    rMultiple: r,
    risk,
    pnl: position.pnl,
  };
}
